import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ESTADOS_TICKET } from '@scm/contratos';
import { api, ErrorApi } from '../lib/api.js';
import { fechaHora } from '../lib/formato.js';
import { CLS_TKT, CLS_VISITA, txt, TXT_ORIGEN, TXT_TKT, TXT_VISITA } from '../lib/enums.js';
import { Pagehead } from '../piezas/Cascaron.jsx';
import { columnasPresentes, ConmutadorVista, Kanban, useVista } from '../piezas/Kanban.jsx';
import { Cargando, Error as AvisoError, Toast, Vacio } from '../piezas/Estados.jsx';
import { useSesion } from '../piezas/Sesion.jsx';

/**
 * Los flujos que puede ensenar el kanban de tickets.
 *
 * Hoy es uno solo: en este esquema el ticket recorre el campo y el staging
 * vive en `equipos`, no en el ticket. Se escribe como LISTA de flujos de
 * todos modos, porque `columnasPresentes` esta hecho para mezclas y el dia
 * que el ticket gane otro recorrido esto es agregar un renglon, no rehacer
 * la pantalla.
 */
const FLUJOS_TICKET = [{ estados: ESTADOS_TICKET }] as const;

interface TicketLista {
  folio: string;
  estado: string;
  origen: string;
  determinante: string;
  sitio: string;
  visitas: number;
  referencia_cliente: string | null;
  cerrado_en: string | null;
  creado_en: string;
}

interface Requisito {
  campo: string;
  motivo: string;
}

interface Accion {
  desde: string;
  hacia: string;
  accion: string;
  requiere: Requisito[];
}

interface Visita {
  numero: number;
  programada_en: string;
  desenlace: string;
  arribo_en: string | null;
  nota: string | null;
}

interface Evidencia {
  nombre: string;
  tipo: string;
  bytes: number;
  a_nombre_de: string | null;
  creado_en: string;
}

interface TicketDetalle {
  ticket: TicketLista & { tiene_evidencia: boolean; inventario_capturado: boolean };
  visitas: Visita[];
  evidencias: Evidencia[];
  /* Las acciones las decide el SERVIDOR con su tabla de transiciones. La
     pantalla no lleva su propia copia de la maquina de estados: una copia se
     desincroniza y entonces el combo ofrece lo que la API va a rechazar. */
  acciones: Accion[];
}

export function Tickets(): JSX.Element {
  const [filas, setFilas] = useState<TicketLista[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [soloAbiertos, setSoloAbiertos] = useState(true);
  const [vista, ponerVista] = useVista('tickets');
  const navegar = useNavigate();

  useEffect(() => {
    setFilas(null);
    api
      .obtener<TicketLista[]>(`/v1/tickets?limite=100${soloAbiertos ? '&abiertos=1' : ''}`)
      .then(({ datos }) => setFilas(datos))
      .catch(setError);
  }, [soloAbiertos]);

  return (
    <>
      <Pagehead />

      <div className="fila wrap" style={{ marginBottom: 12 }}>
        <div className="chips">
          <button
            type="button"
            className={soloAbiertos ? 'chip on' : 'chip'}
            onClick={() => setSoloAbiertos(true)}
            data-prueba="filtro-abiertos"
          >
            Abiertos
          </button>
          <button
            type="button"
            className={!soloAbiertos ? 'chip on' : 'chip'}
            onClick={() => setSoloAbiertos(false)}
          >
            Todos
          </button>
          <ConmutadorVista vista={vista} poner={ponerVista} />
        </div>
      </div>

      {error !== null && <AvisoError error={error} />}

      <div className="card p0">
        {filas === null ? (
          <Cargando que="los tickets" />
        ) : filas.length === 0 ? (
          <Vacio
            mensaje={soloAbiertos ? 'No hay servicios abiertos' : 'Todavia no hay tickets'}
            ayuda="Los servicios nuevos aparecen aqui en cuanto se levantan."
            icono="🎫"
          />
        ) : vista === 'kanban' ? (
          <Kanban
            items={filas}
            estadoDe={(x) => x.estado}
            claveDe={(x) => x.folio}
            /* Las columnas salen del flujo que de verdad esta en la lista:
               una lista de campo no ensena las columnas del staging, y una
               mezclada ensena las dos. */
            columnas={columnasPresentes(
              filas.map((x) => x.estado),
              FLUJOS_TICKET,
            )}
            etiquetas={TXT_TKT}
            clases={CLS_TKT}
            alAbrir={(x) => void navegar(`/tickets/${x.folio}`)}
            tarjeta={(x) => (
              <>
                <div className="folio">{x.folio}</div>
                <b>{x.determinante}</b>
                <div className="muted" style={{ fontSize: 11.5 }}>
                  {x.sitio}
                </div>
                <div className="kan-pie">
                  <span className="tag">{txt(TXT_ORIGEN, x.origen)}</span>
                  <span className="muted">
                    {x.visitas} {x.visitas === 1 ? 'visita' : 'visitas'}
                  </span>
                </div>
              </>
            )}
          />
        ) : (
          <table data-prueba="tabla-tickets">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Determinante / Sitio</th>
                <th>Origen</th>
                <th>Estatus</th>
                <th className="num">Visitas</th>
                <th>Referencia</th>
                <th>Alta</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((t) => (
                <tr
                  key={t.folio}
                  className="click"
                  onClick={() => void navegar(`/tickets/${t.folio}`)}
                  data-prueba="fila-ticket"
                >
                  <td className="folio">{t.folio}</td>
                  <td>
                    <b>{t.determinante}</b>
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      {t.sitio}
                    </div>
                  </td>
                  <td>
                    <span className="tag">{txt(TXT_ORIGEN, t.origen)}</span>
                  </td>
                  <td>
                    <span className={`tag ${CLS_TKT[t.estado] ?? ''}`}>
                      {txt(TXT_TKT, t.estado)}
                    </span>
                  </td>
                  {/* Varias visitas por folio es lo normal: el ticket es el
                      servicio y la visita es el acto de llegar. */}
                  <td className="num">{t.visitas}</td>
                  <td className="muted">{t.referencia_cliente ?? '—'}</td>
                  <td className="muted">{fechaHora(t.creado_en)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

export function Ticket(): JSX.Element {
  const { folio = '' } = useParams();
  const { puede } = useSesion();
  const [d, setD] = useState<TicketDetalle | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [aviso, setAviso] = useState<{ texto: string; malo: boolean } | null>(null);
  const [moviendo, setMoviendo] = useState(false);

  const cargar = useCallback(() => {
    api
      .obtener<TicketDetalle>(`/v1/tickets/${folio}`)
      .then(({ datos }) => setD(datos))
      .catch(setError);
  }, [folio]);

  useEffect(cargar, [cargar]);

  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(null), 2600);
    return () => clearTimeout(t);
  }, [aviso]);

  async function mover(a: Accion): Promise<void> {
    setMoviendo(true);
    setError(null);
    try {
      await api.enviar(`/v1/tickets/${folio}/transicion`, { hacia: a.hacia });
      setAviso({ texto: `Ticket en ${txt(TXT_TKT, a.hacia)}`, malo: false });
      cargar();
    } catch (e) {
      /* Lo que falta lo dice el servidor con su lista completa. La pantalla
         la muestra tal cual en vez de adivinar cual campo pedir primero. */
      if (e instanceof ErrorApi && e.faltan.length > 0) {
        setError(e);
      } else {
        setError(e);
      }
    } finally {
      setMoviendo(false);
    }
  }

  if (error !== null && d === null)
    return (
      <>
        <Pagehead />
        <AvisoError error={error} />
      </>
    );
  if (!d)
    return (
      <>
        <Pagehead />
        <Cargando que="el ticket" />
      </>
    );

  const t = d.ticket;
  const puedeEscribir = puede('MOD_TICKETS', 'escribir');

  return (
    <>
      <Pagehead
        acciones={
          <Link to="/tickets" className="btn ghost">
            ← Tickets
          </Link>
        }
      />

      <div className="fila wrap" style={{ marginBottom: 14 }}>
        <span className="folio" style={{ fontSize: 15, fontWeight: 700 }}>
          {t.folio}
        </span>
        <span className={`tag ${CLS_TKT[t.estado] ?? ''}`} data-prueba="estado-ticket">
          {txt(TXT_TKT, t.estado)}
        </span>
        <span className="tag">{txt(TXT_ORIGEN, t.origen)}</span>
      </div>

      {error !== null && <AvisoError error={error} />}

      {/* El combo de estatus: solo lo que el flujo permite desde aqui.
          Un ticket cerrado no ofrece nada, y eso se dice con un aviso en vez
          de con una barra de botones vacia. */}
      {puedeEscribir && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-h">
            <h2>Mover el servicio</h2>
          </div>
          {d.acciones.length === 0 ? (
            <div className="hint" data-prueba="sin-acciones">
              El servicio esta cerrado y no admite cambios. Una correccion se registra como un
              movimiento nuevo, no reescribiendo el ticket.
            </div>
          ) : (
            <div className="fila wrap">
              {d.acciones.map((a) => (
                <button
                  key={a.hacia}
                  type="button"
                  className={a.hacia === 'TKT_CERRADO' ? 'btn pri' : 'btn'}
                  onClick={() => void mover(a)}
                  disabled={moviendo}
                  data-prueba={`accion-${a.hacia}`}
                  /* Lo que la accion exige va en el title: la persona lo ve
                     antes de presionar en lugar de descubrirlo con un error. */
                  title={
                    a.requiere.length > 0
                      ? `Requiere: ${a.requiere.map((r) => r.motivo).join('; ')}`
                      : undefined
                  }
                >
                  {txt(TXT_TKT, a.hacia)}
                  {a.requiere.length > 0 && (
                    <span className="muted" style={{ fontSize: 10.5 }}>
                      ({a.requiere.length} dato{a.requiere.length > 1 ? 's' : ''})
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid r2">
        <div className="blk">
          <div className="blk-h">Visitas</div>
          <div className="blk-b">
            {d.visitas.length === 0 ? (
              <Vacio mensaje="Sin visitas programadas" icono="📅" />
            ) : (
              <div className="tl">
                {d.visitas.map((v) => (
                  <div key={v.numero} className="tl-it">
                    <div className="fila" style={{ gap: 8 }}>
                      <b>Visita {v.numero}</b>
                      <span className={`tag ${CLS_VISITA[v.desenlace] ?? ''}`}>
                        {txt(TXT_VISITA, v.desenlace)}
                      </span>
                    </div>
                    <div className="muted" style={{ fontSize: 11.5 }}>
                      Programada {fechaHora(v.programada_en)}
                      {v.arribo_en && <> · arribo {fechaHora(v.arribo_en)}</>}
                    </div>
                    {v.nota && <div style={{ fontSize: 12 }}>{v.nota}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="blk">
          <div className="blk-h">Evidencias</div>
          <div className="blk-b">
            {d.evidencias.length === 0 ? (
              <Vacio
                mensaje="Sin evidencias cargadas"
                ayuda="El cierre con firma exige al menos una."
                icono="📷"
              />
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Archivo</th>
                    <th>Por cuenta de</th>
                    <th>Cargada</th>
                  </tr>
                </thead>
                <tbody>
                  {d.evidencias.map((e) => (
                    <tr key={e.nombre}>
                      <td>{e.nombre}</td>
                      {/* Quien capturo y por cuenta de quien: sin app movil el
                          despachador captura por el IDC, y la bitacora tiene
                          que decirlo para servir de evidencia. */}
                      <td className="muted">{e.a_nombre_de ?? '—'}</td>
                      <td className="muted">{fechaHora(e.creado_en)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <div className="sec-title">Requisitos de cierre</div>
      <div className="card">
        <div className="fila wrap" style={{ gap: 18 }}>
          <span className={t.tiene_evidencia ? 'tag t-ok' : 'tag t-hot'}>
            {t.tiene_evidencia ? '✓ Evidencia cargada' : '✗ Falta evidencia'}
          </span>
          <span className={t.inventario_capturado ? 'tag t-ok' : 'tag t-mute'}>
            {t.inventario_capturado ? '✓ Inventario capturado' : 'Inventario sin capturar'}
          </span>
        </div>
      </div>

      {aviso && <Toast mensaje={aviso.texto} malo={aviso.malo} />}
    </>
  );
}
